// src/components/TriggerFlowModal/index.js

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  makeStyles,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 400,
  },
  formControl: {
    minWidth: "100%",
    marginBottom: theme.spacing(2),
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  flowDescription: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  noFlowsMessage: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
  },
  successIcon: {
    color: green[500],
    marginRight: theme.spacing(1),
  },
}));

const TriggerFlowModal = ({ 
  open, 
  onClose, 
  ticketId, 
  ticketStatus,
  fromKanban = false,
  onFlowTriggered,
  onFlowProcessing
}) => {
  const classes = useStyles();
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null); // objeto do fluxo ou null
  const [loading, setLoading] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(true);

  // Carregar fluxos disponíveis
  useEffect(() => {
    const fetchFlows = async () => {
      if (!open) return;
      
      setLoadingFlows(true);
      try {
        const response = await api.get("/flowbuilder");
        
        // A resposta tem a estrutura: { flows: [...] }
        const flowsData = response.data.flows || [];
        
        console.log("Resposta da API flowbuilder:", flowsData);
        
        // Filtrar apenas fluxos ativos
        const activeFlows = flowsData.filter(flow => flow.active === true);
        setFlows(activeFlows);
        
        console.log("Fluxos ativos encontrados:", activeFlows);
      } catch (error) {
        console.error("Erro ao carregar fluxos:", error);
        toastError("Erro ao carregar fluxos disponíveis");
      } finally {
        setLoadingFlows(false);
      }
    };

    fetchFlows();
  }, [open]);

  // Reset ao abrir/fechar modal
  useEffect(() => {
    if (open) {
      setSelectedFlow(null);
    }
  }, [open]);

  // ✅ NOVO: Informar o componente pai sobre o estado de processamento
  useEffect(() => {
    if (onFlowProcessing) {
      onFlowProcessing(loading);
    }
  }, [loading, onFlowProcessing]);

  const handleFlowChange = (event, newValue) => {
    setSelectedFlow(newValue);
  };

  const handleTriggerFlow = async () => {
    if (!selectedFlow || !selectedFlow.id) {
      toast.warning("Selecione um fluxo para disparar");
      return;
    }

    const flowId = selectedFlow.id;
    setLoading(true);
    console.log("🎯 Disparando fluxo:", selectedFlow.name, "para ticket:", ticketId);

    // Fechar o modal logo após o início do disparo (não esperar a resposta completa)
    onClose();
    if (onFlowTriggered) {
      onFlowTriggered({ flowId });
    }

    try {
      const response = await api.post(`/tickets/${ticketId}/trigger-flow`, {
        flowId,
        fromKanban: !!fromKanban
      });

      if (response.data.success) {
        toast.success("Fluxo disparado com sucesso!");
      }
    } catch (error) {
      console.error("❌ Erro ao disparar fluxo:", error);
      const errorMessage = error.response?.data?.error || "Erro ao disparar fluxo";
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const selectedFlowInfo = selectedFlow && selectedFlow.id ? selectedFlow : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        {i18n.t("triggerFlowModal.title")}
      </DialogTitle>
      
      <DialogContent>
        {loadingFlows ? (
          <div className={classes.loadingWrapper}>
            <CircularProgress />
          </div>
        ) : flows.length === 0 ? (
          <div className={classes.noFlowsMessage}>
            <Typography variant="body1">
              {i18n.t("triggerFlowModal.noFlows")}
            </Typography>
          </div>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {i18n.t("triggerFlowModal.description")}
            </Typography>
            
            <Autocomplete
              className={classes.formControl}
              options={flows}
              getOptionLabel={(flow) => flow.name || ""}
              value={selectedFlow}
              onChange={handleFlowChange}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("triggerFlowModal.selectFlow")}
                  placeholder={i18n.t("triggerFlowModal.filterPlaceholder")}
                  variant="outlined"
                />
              )}
              noOptionsText={i18n.t("triggerFlowModal.noOptions")}
            />

            {selectedFlowInfo && (
              <Box className={classes.flowDescription}>
                <Typography variant="caption" display="block">
                  <strong>{i18n.t("triggerFlowModal.selectedFlow")}:</strong> {selectedFlowInfo.name}
                </Typography>
                <Typography variant="caption" display="block">
                  <strong>{i18n.t("triggerFlowModal.flowId")}:</strong> {selectedFlowInfo.id}
                </Typography>
              </Box>
            )}

            {/* ✅ NOVO: Indicador visual quando fluxo está sendo processado */}
            {loading && (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center" 
                mt={2}
                p={2}
                bgcolor="action.hover"
                borderRadius={1}
              >
                <CircularProgress size={20} style={{ marginRight: 8 }} />
                <Typography variant="body2" color="textSecondary">
                  Disparando fluxo... Campo de mensagem temporariamente desabilitado.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose} 
          color="secondary"
          disabled={loading}
        >
          {i18n.t("triggerFlowModal.cancel")}
        </Button>
        <Button
          onClick={handleTriggerFlow}
          color="primary"
          variant="contained"
          disabled={loading || !selectedFlow?.id || loadingFlows || flows.length === 0}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading 
            ? i18n.t("triggerFlowModal.triggering")
            : i18n.t("triggerFlowModal.trigger")
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerFlowModal;